import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { connect } from 'redux-bundler-react';
import { createColumnHelper } from '@tanstack/react-table';
import { Alert, Label } from '@trussworks/react-uswds';

import { EditCell } from '@components/table/tableCellComponents/EditCell';
import { TableCell } from '@components/table/tableCellComponents/TableCell';
import { PermitDateTableCell } from '@components/table/tableCellComponents/PermitDateTableCell';

import StepHeader from '@components/step-header/StepHeader';
import TextAreaInput from '@components/textarea/TextArea';
import SelectInput from '@components/select/Select';
import TanStackTableNew from '@components/table/TanStackTable/TanStackTableNew';
import ExternalLink from '@components/external-link/ExternalLink';
import DateRange from '@components/date-range/DateRange';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import Checkbox from '@components/checkbox/Checkbox';

import PermitsDredgeProject from './PermitsFormGeneralProjectInformation.dredgeProject';
import PermitsDredgeTransportation from './PermitsFormGeneralProjectInformation.dredgeTransportation';
import PermitsDischarges from './PermitsFormGeneralProjectInformation.discharges';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { useRequest } from '@stores';
import { useLookupTableDivisionsAndDistricts } from '@api/services/lookup/hooks';
import { useResetDirtyFields } from '@hooks';

import _isEqual from 'lodash/isEqual';

import { decimalNumberRegex, integerRegex, latRegex, lngRegex, setNumberValue } from '@src/utils/regex';
import { ApplicableStatutoryAuthorities } from '@src/utils/enums';
import { dateBeforeA, dateBeforeB, filterNullEmptyObjects, formatCoordFlt, isDateValid } from '@src/utils/helpers';

import Section from '@src/app-components/section/Section';

export const DaPermitInformationMetadata = {
  sectionName: 'Permit Information',
};

const currentEnv = import.meta.env.VITE_ENVIRONMENT;

const tooltipText = {
  activityBaselineRelationship:
    "The baseline along a state's shoreline is usually established by the mean low water mark (where the shore directly contacts the open sea, the line on the shore reached by the ordinary low tides 33 CFR 329.12(a)(1).  The limit of jurisdiction in the territorial seas refers to the maximum distance from the baseline up to which a coastal state exercises its sovereignty and control; generally 3 miles along the United States coast.",
  doesPilingDischargeFillMaterial:
    'Examples of such activities that have the effect of a discharge of fill material include, but are not limited to, the following: Projects where the pilings are so closely spaced that sedimentation rates would be increased; projects in which the pilings themselves effectively would replace the bottom of a waterbody; projects involving the placement of pilings that would reduce the reach or impair the flow or circulation of waters of the United States; and projects involving the placement of pilings which would result in the adverse alteration or elimination of aquatic functions. 33 CFR 323.3(c)',
  isAnyWorkComplete:
    'Provide any background on any part of the proposed project already completed. Describe the area already developed, structures completed, any dredged or fill material already discharged, the type of material, volume in cubic yards, acres filled, if a wetland or other waterbody (in acres or square feet). If the work was done under an existing USACE permit, identity the authorization (i.e., file number), if possible.',
};

const DaPermitInformation = connect(
  'doUpdatePermitRequest',
  'doUpdatePermitRequestSub',
  'selectRequestFormData',
  'selectPermitData',
  'selectRequestAPIData',
  ({ doUpdatePermitRequest, doUpdatePermitRequestSub, requestFormData, permitData, requestAPIData, stepNo }) => {
    const lookupTableDivisionsAndDistricts = useLookupTableDivisionsAndDistricts();
    const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched } = useRequest();

    const [relatedAppsData, setRelatedAppsData] = useState([]);
    const [relatedAppsDataIsDirty, setRelatedAppsDataIsDirty] = useState(false);
    const [relatedAppsKey, setRelatedAppsKey] = useState(0);
    const [relatedAppsRowErrors, setRelatedAppsRowErrors] = useState(false);
    const [dredgeProjectData, setDredgeProjectData] = useState({});

    const columnHelper = createColumnHelper();
    const district =
      lookupTableDivisionsAndDistricts?.filter((item) => item.orgName === requestFormData?.location?.district)?.[0] ??
      null;

    const prevRelatedAppsDataRef = useRef([]);

    const convertFileNumbersToSelectFormat = useCallback(
      (data) => data?.map((obj) => ({ label: obj.ormFileNumber, value: obj.ormFileNumber })),
      []
    );

    const populateDredgeTypes = useCallback((permitData) => {
      const dredgeTypes = [];
      if (permitData?.dredgeProject?.isDredgeTypeMaintenance) {
        dredgeTypes.push('Maintenance');
      }
      if (permitData?.dredgeProject?.isDredgeTypeNew) {
        dredgeTypes.push('New');
      }
      return dredgeTypes;
    }, []);

    const relatedAppsValidationSchema = yup.object().shape({
      agency: yup.string().required('Field is required'),
      approvalType: yup.string().required('Field is required'),
      certIDNumber: yup.string().required('Field is required'),
      appliedDate: yup.string().required('Please select a date'),
      approvalDate: yup.string().nullable(),
      deniedDate: yup.string().nullable(),
    });

    const relatedAppsTableLength = relatedAppsData?.length;

    const schema = (relatedAppsTableLength) =>
      yup.object().shape(
        {
          requestedPermitType: yup.string().required('Please select an option'),
          developmentType: yup.string().required('Please select an option'),
          hasRelatedAgencyApplications: yup.string().required('Please select an option'),
          relatedAppsTable: yup.boolean().when('hasRelatedAgencyApplications', {
            is: (val) => val === 'true' && relatedAppsTableLength < 1,
            then: () =>
              yup
                .boolean()
                .required(
                  'Previous Permits, Certificates... : At least one certificate, approval, or denial is required'
                ),
          }),
          projectWorkStartDate: yup.string().required('Please select a date'),
          projectWorkEndDate: yup.string().required('Please select a date'),
          isAnyWorkComplete: yup.string().required('Please select an option'),
          completedWorkDescription: yup
            .string()
            .when('isAnyWorkComplete', { is: 'true', then: () => yup.string().required('Field is required') })
            .nullable(),
          navigableWatersAdditionalInfo: yup.string().nullable(),
          applicableStatutoryAuthority: yup.array().min(1, 'Please select at least one option'),
          //IP Specific
          isCoastalDistrict: yup.boolean(),
          isInTerrSeasOrOceanWaters: yup.string().when('isCoastalDistrict', {
            is: true,
            then: (schema) => schema.required('Please select an option'),
            otherwise: (schema) => schema.nullable(),
          }),
          activityBaselineRelationship: yup
            .string()
            .when('isInTerrSeasOrOceanWaters', { is: 'true', then: () => yup.string().required('Field is required') })
            .nullable(),
          //Discharges
          dischargeMaterialSource: yup
            .string()
            .when('applicableStatutoryAuthority', {
              is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section404),
              then: () => yup.string().required('Field is required'),
            })
            .nullable(),
          dischargeReason: yup
            .string()
            .when('applicableStatutoryAuthority', {
              is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section404),
              then: () => yup.string().required('Field is required'),
            })
            .nullable(),
          //DREDGING
          isDredgingNavWaters: yup
            .string()
            .when('applicableStatutoryAuthority', {
              is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section10),
              then: () => yup.string().required('Please select an option'),
            })
            .nullable(),
          materialTypeComposition: yup
            .string()
            .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required('Field is required') })
            .nullable(),
          dredgeMethod: yup
            .string()
            .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required('Field is required') })
            .nullable(),
          constructionMethods: yup
            .string()
            .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required('Field is required') })
            .nullable(),
          estimatedNumberOfEvents: yup
            .string()
            .when(['estimatedNumberOfEvents', 'isDredgingNavWaters'], (estimatedNumberOfEvents, isDredgingNavWaters) =>
              estimatedNumberOfEvents && isDredgingNavWaters === 'true'
                ? yup.string().matches(integerRegex, { message: 'Must be an integer', excludeEmptyString: true })
                : yup.string().nullable()
            ),
          workStartDate: yup
            .string()
            .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required('Please select a date') })
            .nullable(),
          workEndDate: yup
            .string()
            .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required('Please select a date') })
            .nullable()
            .test({
              test: (value, { parent: { isDredgingNavWaters } }) =>
                isDredgingNavWaters === 'true' ? value !== '' : true,
              message: 'Please select a date',
            })
            .test({
              test: (value, { parent: { workStartDate } }) => !value || new Date(value) >= new Date(workStartDate),
              message: 'The end date must be after the start date',
            }),
          durationOfWork: yup
            .string()
            .when('isDredgingNavWaters', {
              is: 'true',
              then: () =>
                yup
                  .number()
                  .required('Field is required')
                  .typeError('Must be an integer.')
                  .positive('Value must be > 0'),
            })
            .nullable(),
          unitOfWork: yup
            .string()
            .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required('Please select an option') })
            .nullable(),
          currentDepth: yup
            .string()
            .when('isDredgingNavWaters', {
              is: 'true',
              then: () => yup.string().required('Field is required').matches(decimalNumberRegex, 'Value is invalid'),
            })
            .nullable(),
          dredgeType: yup
            .array()
            .when('isDredgingNavWaters', {
              is: 'true',
              then: () => yup.array().min(1, 'Please select at least one value'),
            })
            .nullable(),
          lastDredgeCycle: yup
            .string()
            .when('dredgeType', {
              is: (val) => val?.includes('Maintenance'),
              then: () => yup.string().required('Please select a date'),
            })
            .nullable(),
          authorizedLength: yup
            .string()
            .when('dredgeType', {
              is: (val) => val?.length > 0,
              then: () => yup.string().required('Field is required').matches(decimalNumberRegex, 'Field is invalid'),
            })
            .nullable(),
          authorizedWidth: yup
            .string()
            .when('dredgeType', {
              is: (val) => val?.length > 0,
              then: () => yup.string().required('Field is required').matches(decimalNumberRegex, 'Field is invalid'),
            })
            .nullable(),
          authorizedDepth: yup
            .string()
            .when('dredgeType', {
              is: (val) => val?.length > 0,
              then: () => yup.string().required('Field is required').matches(decimalNumberRegex, 'Field is invalid'),
            })
            .nullable(),
          disposalPlan: yup
            .string()
            .when('dredgeType', {
              is: (val) => val?.length > 0,
              then: () => yup.string().required('Value is required'),
            })
            .nullable(),
          estNumDisposalTrips: yup
            .string()
            .when(['estNumDisposalTrips', 'dredgeType'], (estNumDisposalTrips, dredgeType) =>
              estNumDisposalTrips && dredgeType?.length > 0
                ? yup.string().matches(integerRegex, { message: 'Must be an integer', excludeEmptyString: true })
                : yup.string().nullable()
            ),
          dredgeDisposalLatitude: yup
            .mixed()
            .when('dredgeType', {
              is: (val) => val?.length > 0,
              then: () =>
                yup
                  .string()
                  .required('Field is required')
                  .matches(latRegex, 'Format is incorrect. Must be +-XX.XXXXXX and include at least 6 decimal places.'),
            })
            .nullable(),
          dredgeDisposalLongitude: yup
            .mixed()
            .when('dredgeType', {
              is: (val) => val?.length > 0,
              then: () =>
                yup
                  .string()
                  .required('Field is required')
                  .matches(
                    lngRegex,
                    'Format is incorrect. Must be +-XXX.XXXXXX and include at least 6 decimal places.'
                  ),
            })
            .nullable(),
          // DREDGE TRANSPORTATION
          isSiteDesignatedForDISP: yup
            .string()
            .when('applicableStatutoryAuthority', {
              is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
              then: () => yup.string().required('Please select an option'),
            })
            .nullable(),
          disposalSiteCharacteristics: yup
            .string()
            .when('isSiteDesignatedForDISP', { is: 'false', then: () => yup.string().required('Please enter a value') })
            .nullable(),
          knownDredgeMaterialDischarges: yup
            .string()
            .when('applicableStatutoryAuthority', {
              is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
              then: () => yup.string().required('Field is required'),
            })
            .nullable(),
          previousDisposals: yup
            .string()
            .when('applicableStatutoryAuthority', {
              is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
              then: () => yup.string().required('Field is required'),
            })
            .nullable(),
          estNumberOfMonthsOnSite: yup
            .string()
            .when('applicableStatutoryAuthority', {
              is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
              then: () => yup.string().matches(integerRegex, 'Must be an integer').required('Field is required'),
            })
            .nullable(),
          materialSource: yup
            .string()
            .when('applicableStatutoryAuthority', {
              is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
              then: () => yup.string().required('Field is required'),
            })
            .nullable(),
          materialTypeAndComp: yup
            .string()
            .when('applicableStatutoryAuthority', {
              is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
              then: () => yup.string().required('Field is required'),
            })
            .nullable(),
          transportationAndDisposalMethod: yup
            .string()
            .when('applicableStatutoryAuthority', {
              is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section103),
              then: () => yup.string().required('Field is required'),
            })
            .nullable(),
        },
        [
          ['estimatedNumberOfEvents', 'estimatedNumberOfEvents'],
          ['estNumDisposalTrips', 'estNumDisposalTrips'],
          ['existingVesselSlips', 'existingVesselSlips'],
        ]
      );

    const defaultValues = {
      projectName: requestFormData?.projectName,
      hasPreviousFileNumber:
        requestFormData?.previousFileNumbers?.length > 0 || requestFormData?.hasPreviousFileNumber === true
          ? 'true'
          : 'false',
      previousFileNumbers:
        requestFormData?.previousFileNumbers?.length > 0
          ? convertFileNumbersToSelectFormat(requestFormData.previousFileNumbers)
          : [],
      requestedPermitType: permitData?.requestedPermitType ?? '',
      developmentType: permitData?.developmentType ?? '',
      hasRelatedAgencyApplications:
        permitData?.hasRelatedAgencyApplications !== undefined && permitData?.hasRelatedAgencyApplications !== null
          ? permitData.hasRelatedAgencyApplications
            ? 'true'
            : 'false'
          : '',
      projectDescription: requestFormData?.projectDescription ?? '',
      projectPurpose: requestFormData?.projectPurpose ?? '',
      natureOfActivity: requestFormData?.natureOfActivity ?? '',
      projectWorkStartDate: permitData?.workStartDate?.split('T')?.[0] ?? '',
      projectWorkEndDate: permitData?.workEndDate?.split('T')?.[0] ?? '',
      applicableStatutoryAuthority: permitData?.applicableStatutoryAuthorities ?? [],
      relatedAgencyApplications: permitData?.relatedAgencyApplications
        ? permitData?.relatedAgencyApplications?.map((item) => ({
            ...item,
            appliedDate: item?.appliedDate?.split('T')?.[0],
            approvedDate: item?.approvedDate?.split('T')?.[0],
            deniedDate: item?.deniedDate?.split('T')?.[0],
          }))
        : [],
      navigableWatersAdditionalInfo: permitData?.navigableWatersAdditionalInfo ?? '',
      isAnyWorkComplete:
        permitData?.isAnyWorkComplete !== undefined && permitData?.isAnyWorkComplete !== null
          ? permitData.isAnyWorkComplete
            ? 'true'
            : 'false'
          : '',
      completedWorkDescription: permitData?.completedWorkDescription ?? '',
      // Dredge Project
      isDredgingNavWaters:
        permitData?.isDredgingNavWaters !== undefined && permitData?.isDredgingNavWaters !== null
          ? permitData.isDredgingNavWaters
            ? 'true'
            : 'false'
          : '',
      materialTypeComposition: permitData?.dredgeProject?.materialTypeComposition ?? '',
      dredgeMethod: permitData?.dredgeProject?.dredgeMethod ?? '',
      constructionMethods: permitData?.dredgeProject?.constructionMethods ?? '',
      estimatedNumberOfEvents: permitData?.dredgeProject?.estimatedNumberOfEvents ?? '',
      workStartDate: permitData?.dredgeProject?.workStartDate?.split('T')?.[0] ?? '',
      workEndDate: permitData?.dredgeProject?.workEndDate?.split('T')?.[0] ?? '',
      durationOfWork: permitData?.dredgeProject?.durationOfWork ?? null,
      unitOfWork: permitData?.dredgeProject?.unitOfWork ?? '',
      currentDepth: permitData?.dredgeProject?.currentDepth ?? '',
      dredgeType: populateDredgeTypes(permitData),
      lastDredgeCycle: permitData?.dredgeProject?.lastDredgeCycle?.split('T')?.[0] ?? '',
      authorizedLength: permitData?.dredgeProject?.authorizedLength ?? '',
      authorizedWidth: permitData?.dredgeProject?.authorizedWidth ?? '',
      authorizedDepth: permitData?.dredgeProject?.authorizedDepth ?? '',
      disposalPlan: permitData?.dredgeProject?.disposalPlan ?? '',
      estNumDisposalTrips: permitData?.dredgeProject?.estNumDisposalTrips ?? '',
      dredgeDisposalLatitude:
        typeof permitData?.dredgeProject?.dredgeDisposalLatitude === 'number'
          ? permitData?.dredgeProject?.dredgeDisposalLatitude?.toFixed(7)
          : (permitData?.dredgeProject?.dredgeDisposalLatitude ?? ''),
      dredgeDisposalLongitude:
        typeof permitData?.dredgeProject?.dredgeDisposalLongitude === 'number'
          ? permitData?.dredgeProject?.dredgeDisposalLongitude?.toFixed(7)
          : (permitData?.dredgeProject?.dredgeDisposalLongitude ?? ''),
      // Pile Driving and Coastal Shoreline Stabilization Manual Overwrite
      includesInstallPiling:
        permitData?.includesInstallPiling !== undefined && permitData?.includesInstallPiling !== ''
          ? permitData.includesInstallPiling
            ? 'true'
            : 'false'
          : 'false',
      includesShorelineStabilization:
        permitData?.includesShorelineStabilization !== undefined && permitData?.includesShorelineStabilization !== ''
          ? permitData.includesShorelineStabilization
            ? 'true'
            : 'false'
          : 'false',
      // Dredge Transportation
      isSiteDesignatedForDISP: permitData?.dredgedTransportation?.isSiteDesignatedForDISP?.toString() || '',
      knownDredgeMaterialDischarges: permitData?.dredgedTransportation?.knownDredgeMaterialDischarges ?? '',
      disposalSiteCharacteristics: permitData?.dredgedTransportation?.disposalSiteCharacteristics ?? '',
      previousDisposals: permitData?.dredgedTransportation?.previousDisposals ?? '',
      estNumberOfMonthsOnSite: permitData?.dredgedTransportation?.estNumberOfMonthsOnSite ?? '',
      materialSource: permitData?.dredgedTransportation?.materialSource ?? '',
      materialTypeAndComp: permitData?.dredgedTransportation?.materialTypeAndComp ?? '',
      transportationAndDisposalMethod: permitData?.dredgedTransportation?.transportationAndDisposalMethod ?? '',
      //Discharges
      dischargeMaterialSource: permitData?.discharges_404?.materialSource ?? '',
      dischargeReason: permitData?.discharges_404?.dischargeReason ?? '',
      //IP Specific
      isCoastalDistrict: district?.isCoastalDistrict ?? false,
      isInTerrSeasOrOceanWaters:
        permitData?.isInTerrSeasOrOceanWaters !== undefined && permitData?.isInTerrSeasOrOceanWaters !== null
          ? permitData.isInTerrSeasOrOceanWaters
            ? 'true'
            : 'false'
          : '',
      activityBaselineRelationship: permitData?.activityBaselineRelationship ?? '',
    };

    const methods = useForm({
      resolver: yupResolver(schema(relatedAppsTableLength)),
      mode: 'onBlur',
      defaultValues: defaultValues,
    });
    const {
      formState: { errors, dirtyFields },
      setValue,
      watch,
      getValues,
      setError,
      clearErrors,
      trigger,
      reset,
    } = methods;
    const isAnyWorkComplete = watch('isAnyWorkComplete');
    const applicableStatutoryAuthority = watch('applicableStatutoryAuthority');
    const isDredgingNavWaters = watch('isDredgingNavWaters');
    const dredgeType = watch('dredgeType');
    const hasRelatedAgencyApplications = watch('hasRelatedAgencyApplications');
    const isSiteDesignatedForDISP = watch('isSiteDesignatedForDISP');
    const isInTerrSeasOrOceanWaters = watch('isInTerrSeasOrOceanWaters');
    const isCoastalDistrict = watch('isCoastalDistrict');
    const isDirty = Object.keys(dirtyFields).length > 0 || relatedAppsDataIsDirty;
    const relatedAppsValid =
      hasRelatedAgencyApplications === 'true' ? relatedAppsData?.length > 0 && !relatedAppsRowErrors : true;
    const isValid = Object.keys(errors).length === 0 && relatedAppsValid;
    const isTouched = isSectionTouched(stepNo);

    useEffect(() => {
      updateSection(stepNo, { dirty: isDirty, valid: isValid });
    }, [stepNo, isDirty, isValid, relatedAppsDataIsDirty, updateSection]);

    useEffect(() => {
      const tableDataChanged = !_isEqual(relatedAppsData, prevRelatedAppsDataRef.current);
      tableDataChanged && setRelatedAppsDataIsDirty(true);
    }, [relatedAppsData]);

    //Reset the dirty states of the fields after a save. Only trigger validation for steps that have been touched.
    useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    const relatedAppsColumns = useMemo(
      () => [
        columnHelper.display({
          id: 'edit',
          cell: isReadOnly ? <></> : EditCell,
          size: 36,
          enableResizing: false,
          enableSorting: false,
        }),
        columnHelper.accessor('agency', {
          header: 'Agency Name',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 300,
          meta: {
            required: true,
          },
        }),
        columnHelper.accessor('approvalType', {
          header: 'Type of Approval',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 300,
          meta: {
            required: true,
          },
        }),
        columnHelper.accessor('certIDNumber', {
          header: 'Identification Number',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 200,
          meta: {
            required: true,
            maxLength: 100,
          },
        }),
        columnHelper.accessor('appliedDate', {
          header: 'Date Applied',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : PermitDateTableCell,
          size: 150,
          meta: {
            type: 'date',
            min: dateBeforeB,
            required: true,
          },
        }),
        columnHelper.accessor('approvedDate', {
          header: 'Date Approved',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : PermitDateTableCell,
          size: 150,
          meta: {
            type: 'date',
            min: dateBeforeB,
            required: false,
          },
        }),
        columnHelper.accessor('deniedDate', {
          header: 'Date Denied',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : PermitDateTableCell,
          size: 150,
          cellDisabled: true,
          meta: {
            type: 'date',
            min: dateBeforeB,
            required: false,
          },
        }),
      ],
      [columnHelper, isReadOnly]
    );

    const updateRelatedAppsData = useCallback(
      (rowIndex, columnId, updatedValue) => {
        setRelatedAppsData((oldData) => {
          const newData = oldData ? [...oldData] : null;
          if (newData && newData[rowIndex]) {
            // Update properties
            newData[rowIndex] = {
              ...newData[rowIndex],
              [columnId]: updatedValue,
            };
            return newData;
          }
        });
      },
      [setRelatedAppsData]
    );

    const removeMultipleRelatedAppRows = useCallback(
      (indicesToRemove) => {
        setRelatedAppsData((oldData) => {
          const newRows = oldData.filter((_, index) => !indicesToRemove.includes(index));
          return newRows;
        });
        setRelatedAppsKey((old) => old + 1);
      },
      [setRelatedAppsData, setRelatedAppsKey]
    );

    useEffect(() => {
      relatedAppsData && doUpdatePermitRequest({ relatedAgencyApplications: relatedAppsData });
    }, [relatedAppsData, doUpdatePermitRequest]);

    // Update Dredge Project data in Permit Request Body
    useEffect(() => {
      dredgeProjectData && doUpdatePermitRequest({ dredgeProject: dredgeProjectData });
    }, [dredgeProjectData, doUpdatePermitRequest]);

    // Load data from API
    useEffect(() => {
      if (requestAPIData?.request?.permits?.[0]?.relatedAgencyApplications) {
        const relatedAppsData = requestAPIData?.request?.permits?.[0]?.relatedAgencyApplications.map((item) => ({
          ...item,
          appliedDate: isDateValid(item?.appliedDate) === true ? item?.appliedDate?.split('T')?.[0] : '',
          approvedDate: isDateValid(item?.approvedDate) === true ? item?.approvedDate?.split('T')?.[0] : '',
          deniedDate: isDateValid(item?.deniedDate) === true ? item?.deniedDate?.split('T')?.[0] : '',
        }));
        setRelatedAppsData(relatedAppsData);
        prevRelatedAppsDataRef.current = relatedAppsData;
      }

      if (requestAPIData?.request?.permits?.[0]?.dredgeProject) {
        setDredgeProjectData(requestAPIData?.request?.permits?.[0]?.dredgeProject);
      }
      isTouched && trigger();
    }, [requestAPIData, isTouched, trigger]);

    const handleChange = (e) => {
      if (e?.target?.name === 'dredgeType') trigger();

      // Populate General Project Info nested sections

      const populateDredgeProject = () => {
        const values = getValues();
        // Dredge Project
        const dredgeProjectObj = {
          materialTypeComposition: values?.materialTypeComposition ?? '',
          dredgeMethod: values?.dredgeMethod ?? '',
          constructionMethods: values?.constructionMethods ?? '',
          estimatedNumberOfEvents: setNumberValue(values?.estimatedNumberOfEvents),
          workStartDate: isDateValid(values?.workStartDate) ? values?.workStartDate : '',
          workEndDate: isDateValid(values?.workEndDate) ? values?.workEndDate : '',
          durationOfWork: setNumberValue(values?.durationOfWork),
          unitOfWork: values?.unitOfWork ?? '',
          currentDepth: setNumberValue(values?.currentDepth, false),
          isDredgeTypeMaintenance:
            values?.dredgeType.length > 0 ? (values?.dredgeType?.includes('Maintenance') ? true : false) : null,
          isDredgeTypeNew: values?.dredgeType.length > 0 ? (values?.dredgeType?.includes('New') ? true : false) : null,
          lastDredgeCycle: values?.lastDredgeCycle ?? '',
          authorizedLength: setNumberValue(values?.authorizedLength, false),
          authorizedWidth: setNumberValue(values?.authorizedWidth, false),
          authorizedDepth: setNumberValue(values?.authorizedDepth, false),
          disposalPlan: values?.disposalPlan ?? '',
          estNumDisposalTrips: setNumberValue(values?.estNumDisposalTrips),
          dredgeDisposalLatitude: formatCoordFlt(values?.dredgeDisposalLatitude) ?? null,
          dredgeDisposalLongitude: formatCoordFlt(values?.dredgeDisposalLongitude) ?? null,
        };
        // Check if values in obj are all null/empty string
        return filterNullEmptyObjects(dredgeProjectObj) !== null ? { ...dredgeProjectData, ...dredgeProjectObj } : null;
      };

      const populateDischarges = () => {
        const values = getValues();
        const discharges404Obj = {
          materialSource: values?.dischargeMaterialSource ?? '',
          dischargeReason: values?.dischargeReason ?? '',
        };
        return filterNullEmptyObjects(discharges404Obj) !== null ? { ...discharges404Obj } : null;
      };

      const populateDredgedTransportation = () => {
        const values = getValues();
        const dredgedTransportationObj = {
          isSiteDesignatedForDISP:
            values?.isSiteDesignatedForDISP === 'true'
              ? true
              : values?.isSiteDesignatedForDISP === 'false'
                ? false
                : '',
          knownDredgeMaterialDischarges: values?.knownDredgeMaterialDischarges ?? '',
          disposalSiteCharacteristics: values?.disposalSiteCharacteristics ?? '',
          previousDisposals: values?.previousDisposals ?? '',
          estNumberOfMonthsOnSite: setNumberValue(values?.estNumberOfMonthsOnSite),
          materialSource: values?.materialSource ?? '',
          materialTypeAndComp: values?.materialTypeAndComp ?? '',
          transportationAndDisposalMethod: values?.transportationAndDisposalMethod ?? '',
        };
        return filterNullEmptyObjects(dredgedTransportationObj) !== null ? { ...dredgedTransportationObj } : null;
      };

      // Map & insert values into GP data modal
      if (!isReadOnly) {
        const values = getValues();
        const gpValues = {
          hasRelatedAgencyApplications:
            values?.hasRelatedAgencyApplications === 'true'
              ? true
              : values?.hasRelatedAgencyApplications === 'false'
                ? false
                : null,
          requestedPermitType: values?.requestedPermitType ?? '',
          developmentType: values?.developmentType ?? '',
          workStartDate: isDateValid(values?.projectWorkStartDate) === true ? values?.projectWorkStartDate : '',
          workEndDate: isDateValid(values?.projectWorkEndDate) ? values?.projectWorkEndDate : '',
          navigableWatersAdditionalInfo: values?.navigableWatersAdditionalInfo ?? '',
          isAnyWorkComplete:
            values?.isAnyWorkComplete === 'true' ? true : values?.isAnyWorkComplete === 'false' ? false : null,
          completedWorkDescription: values?.completedWorkDescription ?? '',
          isInTerrSeasOrOceanWaters:
            values?.isInTerrSeasOrOceanWaters === 'true'
              ? true
              : values?.isInTerrSeasOrOceanWaters === 'false'
                ? false
                : null,
          activityBaselineRelationship: values?.activityBaselineRelationship ?? '',
          isDredgingNavWaters:
            values?.isDredgingNavWaters === 'true' ? true : values?.isDredgingNavWaters === 'false' ? false : null,
          includesInstallPiling: false,
          includesShorelineStabilization: false,
          applicableStatutoryAuthorities: values?.applicableStatutoryAuthority ?? [],
          dredgeProject: populateDredgeProject() ?? null,
          discharges_404: populateDischarges() ?? null,
          dredgedTransportation: populateDredgedTransportation() ?? null,
        };
        doUpdatePermitRequest(gpValues);
      }
    };

    /* clear out sections when dropdowns are false */
    useEffect(() => {
      if (isAnyWorkComplete === 'false' || isAnyWorkComplete === '') {
        setValue('completedWorkDescription', '', { shouldValidate: isTouched });
        doUpdatePermitRequest({ completedWorkDescription: '' });
      }
    }, [isAnyWorkComplete, isTouched, setValue, doUpdatePermitRequest]);

    useEffect(() => {
      if (isInTerrSeasOrOceanWaters === 'false' || isInTerrSeasOrOceanWaters === '') {
        setValue('activityBaselineRelationship', '', { shouldValidate: isTouched });
        doUpdatePermitRequest({ activityBaselineRelationship: '' });
      }
    }, [isInTerrSeasOrOceanWaters, isTouched, setValue, doUpdatePermitRequest]);

    useEffect(() => {
      if (hasRelatedAgencyApplications === 'false' || hasRelatedAgencyApplications === '') {
        setRelatedAppsData([]);
        doUpdatePermitRequest({ relatedAgencyApplications: [] });
      }
    }, [hasRelatedAgencyApplications, setValue, doUpdatePermitRequest]);

    useEffect(() => {
      if (isTouched) {
        if (relatedAppsData?.length < 1 && hasRelatedAgencyApplications === 'true') {
          setError('relatedAppsTable', {
            type: 'custom',
            message: 'Previous Permits, Certificates... : At least one certificate, approval or denial is required',
          });
        } else if (relatedAppsRowErrors) {
          setError('relatedAppsTable', {
            type: 'custom',
            message: 'Previous Permits, Certificates... : Table contains invalid rows',
          });
        } else if (!relatedAppsRowErrors && relatedAppsData?.length > 0) {
          clearErrors('relatedAppsTable');
        } else {
          clearErrors('relatedAppsTable');
        }
      }
    }, [hasRelatedAgencyApplications, relatedAppsRowErrors, relatedAppsData, isTouched, clearErrors, setError]);

    useEffect(() => {
      if (!applicableStatutoryAuthority.includes(ApplicableStatutoryAuthorities.Section404)) {
        setValue('dischargeMaterialSource', '', { shouldValidate: isTouched });
        setValue('dischargeReason', '', { shouldValidate: isTouched });
        doUpdatePermitRequest({ discharges_404: null });
      }
    }, [applicableStatutoryAuthority, isTouched, doUpdatePermitRequest, setValue]);

    useEffect(() => {
      if (!applicableStatutoryAuthority.includes(ApplicableStatutoryAuthorities.Section10)) {
        setValue('isDredgingNavWaters', '', { shouldValidate: isTouched });
        doUpdatePermitRequest({ isDredgingNavWaters: null });
      }
    }, [applicableStatutoryAuthority, isTouched, doUpdatePermitRequest, setValue]);

    useEffect(() => {
      if (!applicableStatutoryAuthority.includes(ApplicableStatutoryAuthorities.Section103)) {
        setValue('isSiteDesignatedForDISP', '', { shouldValidate: isTouched });
        setValue('knownDredgeMaterialDischarges', '', { shouldValidate: isTouched });
        setValue('disposalSiteCharacteristics', '', { shouldValidate: isTouched });
        setValue('previousDisposals', '', { shouldValidate: isTouched });
        setValue('estNumberOfMonthsOnSite', '', { shouldValidate: isTouched });
        setValue('materialSource', '', { shouldValidate: isTouched });
        setValue('materialTypeAndComp', '', { shouldValidate: isTouched });
        doUpdatePermitRequest({ dredgedTransportation: null });
      }
    }, [applicableStatutoryAuthority, isTouched, doUpdatePermitRequest, setValue]);

    useEffect(() => {
      if (applicableStatutoryAuthority.length === 0) {
        setValue('isDredgingNavWaters', '', { shouldValidate: isTouched });
        doUpdatePermitRequest({ isDredgingNavWaters: null });
      }
    }, [applicableStatutoryAuthority, isTouched, doUpdatePermitRequest, setValue]);

    // Dredging
    useEffect(() => {
      if (dredgeType.length === 0) {
        setValue('lastDredgeCycle', '', { shouldValidate: isTouched });
        setValue('authorizedLength', '', { shouldValidate: isTouched });
        setValue('authorizedWidth', '', { shouldValidate: isTouched });
        setValue('authorizedDepth', '', { shouldValidate: isTouched });
        setValue('disposalPlan', '', { shouldValidate: isTouched });
        setValue('estNumDisposalTrips', '', { shouldValidate: isTouched });
        setValue('dredgeDisposalLatitude', '', { shouldValidate: isTouched });
        setValue('dredgeDisposalLongitude', '', { shouldValidate: isTouched });
        doUpdatePermitRequestSub('dredgeProject', { lastDredgeCycle: null });
        doUpdatePermitRequestSub('dredgeProject', { authorizedLength: null });
        doUpdatePermitRequestSub('dredgeProject', { authorizedWidth: null });
        doUpdatePermitRequestSub('dredgeProject', { authorizedDepth: null });
        doUpdatePermitRequestSub('dredgeProject', { disposalPlan: '' });
        doUpdatePermitRequestSub('dredgeProject', { estNumDisposalTrips: null });
        doUpdatePermitRequestSub('dredgeProject', { dredgeDisposalLatitude: null });
        doUpdatePermitRequestSub('dredgeProject', { dredgeDisposalLongitude: null });
      }
    }, [dredgeType, isTouched, setValue, doUpdatePermitRequestSub]);

    useEffect(() => {
      if (isDredgingNavWaters === 'false' || isDredgingNavWaters === '') {
        setValue('materialTypeComposition', '', { shouldValidate: isTouched });
        setValue('dredgeMethod', '', { shouldValidate: isTouched });
        setValue('constructionMethods', '', { shouldValidate: isTouched });
        setValue('estimatedNumberOfEvents', '', { shouldValidate: isTouched });
        setValue('workStartDate', '', { shouldValidate: isTouched });
        setValue('workEndDate', '', { shouldValidate: isTouched });
        setValue('durationOfWork', '', { shouldValidate: isTouched });
        setValue('unitOfWork', '', { shouldValidate: isTouched });
        setValue('currentDepth', '', { shouldValidate: isTouched });
        setValue('dredgeType', [], { shouldValidate: isTouched });
        doUpdatePermitRequestSub('dredgeProject', { materialTypeComposition: '' });
        doUpdatePermitRequestSub('dredgeProject', { dredgeMethod: '' });
        doUpdatePermitRequestSub('dredgeProject', { constructionMethods: '' });
        doUpdatePermitRequestSub('dredgeProject', { estimatedNumberOfEvents: null });
        doUpdatePermitRequestSub('dredgeProject', { workStartDate: '' });
        doUpdatePermitRequestSub('dredgeProject', { workEndDate: '' });
        doUpdatePermitRequestSub('dredgeProject', { durationOfWork: null });
        doUpdatePermitRequestSub('dredgeProject', { unitOfWork: '' });
        doUpdatePermitRequestSub('dredgeProject', { currentDepth: null });
        doUpdatePermitRequestSub('dredgeProject', { isDredgeTypeMaintenance: null });
        doUpdatePermitRequestSub('dredgeProject', { isDredgeTypeNew: null });
        // Reset all dredge project data
        setDredgeProjectData({});
      }
    }, [isDredgingNavWaters, isTouched, setValue, doUpdatePermitRequestSub]);

    //Dredge Transportation
    useEffect(() => {
      if (!applicableStatutoryAuthority.includes(ApplicableStatutoryAuthorities.Section103)) {
        setValue('isSiteDesignatedForDISP', '', { shouldValidate: isTouched });
        setValue('disposalSiteCharacteristics', '', { shouldValidate: isTouched });
        setValue('previousDisposals', '', { shouldValidate: isTouched });
        setValue('estNumberOfMonthsOnSite', '', { shouldValidate: isTouched });
        setValue('materialSource', '', { shouldValidate: isTouched });
        setValue('materialTypeAndComp', '', { shouldValidate: isTouched });
        setValue('transportationAndDisposalMethod', '', { shouldValidate: isTouched });

        doUpdatePermitRequestSub('dredgedTransportation', { isSiteDesignatedForDISP: '' });
        doUpdatePermitRequestSub('dredgedTransportation', { disposalSiteCharacteristics: '' });
        doUpdatePermitRequestSub('dredgedTransportation', { previousDisposals: '' });
        doUpdatePermitRequestSub('dredgedTransportation', { estNumberOfMonthsOnSite: null });
        doUpdatePermitRequestSub('dredgedTransportation', { materialSource: '' });
        doUpdatePermitRequestSub('dredgedTransportation', { materialTypeAndComp: '' });
        doUpdatePermitRequestSub('dredgedTransportation', { transportationAndDisposalMethod: '' });
      }
    }, [applicableStatutoryAuthority, isTouched, doUpdatePermitRequestSub, setValue]);

    useEffect(() => {
      if (isSiteDesignatedForDISP === 'true') {
        setValue('disposalSiteCharacteristics', '', { shouldValidate: isTouched });
        doUpdatePermitRequestSub('dredgedTransportation', { disposalSiteCharacteristics: '' });
      }
    }, [isSiteDesignatedForDISP, isTouched, doUpdatePermitRequestSub, setValue]);

    useEffect(() => {
      const relatedAppsValid =
        hasRelatedAgencyApplications === 'true' ? relatedAppsData?.length > 0 && !relatedAppsRowErrors : true;
      const sectionValid = isValid && relatedAppsValid;
      updateSection(stepNo, { valid: sectionValid });
    }, [
      isValid,
      updateSection,
      hasRelatedAgencyApplications,
      stepNo,
      relatedAppsData?.length,
      relatedAppsRowErrors,
      isReadOnly,
    ]);

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}
        <StepHeader text='Permit Information' />
        <div>
          <SelectInput
            className='width-50'
            fullWidth={false}
            name='requestedPermitType'
            label="Select the type of permit you're requesting"
            onChange={handleChange}
            required
            readOnly={isReadOnly}
          >
            <option key='2' value='Standard Permit'>
              Standard Permit
            </option>
            <option key='3' value='Letter of Permission'>
              Letter of Permission
            </option>
            <option key='4' value='General Permit'>
              General Permit
            </option>
          </SelectInput>
        </div>
        <div>
          <SelectInput
            className='width-50'
            fullWidth={false}
            name='developmentType'
            label='Select a development type'
            required
            tooltip='Fees are required for permits under Section 404 of the Clean Water Act, Section 103 of the Marine Protection, Research and Sanctuaries Act of 1972, as amended, and Sections 9 and 10 of the Rivers and Harbors Act of 1899. A fee of $100.00 will be charged when the planned or ultimate purpose of the project is commercial or industrial in nature and is in support of operations that charge for the production, distribution or sale of goods or services. A $10.00 fee will be charged for permit applications when the proposed work is non-commercial in nature and would provide personal benefits that have no connection with a commercial enterprise. The final decision as to the basis for a fee (commercial vs. non-commercial) shall be solely the responsibility of the U.S. Army Corps of Engineers. No fee will be charged if the applicant withdraws the application at any time prior to issuance of the permit or if the permit is denied. Collection of the fee will be deferred until the proposed activity has been determined to not be contrary to the public interest. Multiple fees are not to be charged if more than one law is applicable. Any modification significant enough to require publication of a public notice will also require a fee. No fee will be assessed when a permit is transferred from one property owner to another. No fees will be charged for time extensions, general permits, or letters of permission. Agencies or instrumentalities of federal, state, or local governments will not be required to pay any fee in connection with permits. 33 CFR 325.1 Payment of permit fees is accomplished through pay.gov and must be paid before a permit can be issued. Districts will contact you on how to do this - https://www.pay.gov/public/form/start/996412796'
            onChange={handleChange}
            readOnly={isReadOnly}
          >
            <option key='2' value='Commercial'>
              Commercial
            </option>
            <option key='3' value='Non-Commercial'>
              Non-Commercial
            </option>
            <option key='4' value='Government/Tribal'>
              Government/Tribal
            </option>
          </SelectInput>
        </div>
        <div>
          <SelectInput
            className='width-50'
            fullWidth={false}
            name='hasRelatedAgencyApplications'
            label='Have other Certificates or Approvals/Denials been received from other Federal, State, or Local Agencies for Work Described in This Application?'
            required
            tooltip='You may need the approval of other federal, state, or local agencies for your project. Identify any applications you have submitted and the status, if any (approved or denied), of each application. You need not have obtained all other permits before applying for a USACE permit.'
            onChange={handleChange}
            readOnly={isReadOnly}
          >
            <option key='2' value='true'>
              Yes
            </option>
            <option key='3' value='false'>
              No
            </option>
          </SelectInput>
        </div>
        {hasRelatedAgencyApplications === 'true' && (
          <fieldset className='margin-top-3 padding-0' id='relatedAppsTableFieldset'>
            <Label className='margin-0 required' htmlFor='relatedAppsTableFieldset'>
              List any certificates or approvals/denials that have been received from other federal, state, or local
              agencies for work described in this application.
            </Label>
            <TanStackTableNew
              addRow={() => setRelatedAppsData((prev) => (prev ? [...prev, {}] : [{}]))}
              columns={relatedAppsColumns}
              data={relatedAppsData}
              hideDrag={true}
              initialTableState={{}}
              isInputTable={true}
              isReadOnly={isReadOnly}
              key={relatedAppsKey}
              placeholderClick={() => setRelatedAppsData((prev) => (prev ? [...prev, {}] : [{}]))}
              placeholderText='No certificates or approvals/denials found, Please click below to add a certificate,  approvals and/or denial'
              removeMultipleRows={removeMultipleRelatedAppRows}
              rowErrorCallback={setRelatedAppsRowErrors}
              updateSourceData={updateRelatedAppsData}
              validationSchema={relatedAppsValidationSchema}
            />
          </fieldset>
        )}
        <fieldset className='margin-top-3 padding-0' id='projectWorkDates'>
          <Label htmlFor='projectWorkDates' className='margin-0'>
            Approximate dates you plan to both begin and complete all work:
          </Label>
          <DateRange
            nameBefore='projectWorkStartDate'
            nameAfter='projectWorkEndDate'
            labelBefore='Project Start Date'
            labelAfter='Project End Date'
            dateBefore={dateBeforeA}
            onChange={handleChange}
            readOnly={isReadOnly}
            required
          />
        </fieldset>
        <div>
          <SelectInput
            className='width-50'
            fullWidth={false}
            name='isAnyWorkComplete'
            label='Is any portion of the work complete?'
            tooltip={tooltipText.isAnyWorkComplete}
            onChange={handleChange}
            readOnly={isReadOnly}
            required
          >
            <option key='2' value='true'>
              Yes
            </option>
            <option key='3' value='false'>
              No
            </option>
          </SelectInput>
        </div>
        {(isAnyWorkComplete === 'true' || isAnyWorkComplete === true) && (
          <div>
            <TextAreaInput
              name='completedWorkDescription'
              label='Describe the completed work'
              onChange={handleChange}
              readOnly={isReadOnly}
              required
            />
          </div>
        )}
        <fieldset className='padding-0 margin-top-1' id='applicableStatutoryAuthority'>
          <Label className='margin-bottom-1 required' htmlFor='applicableStatutoryAuthority'>
            <span id='applicableStatutoryAuthority_label'>Select Applicable Statutory Authority</span>
          </Label>
          <Alert className='margin-bottom-2' type='info'>
            If you're unsure of which authorities apply to your project, you can{' '}
            <ExternalLink
              href={
                currentEnv === 'local' || currentEnv === 'development' ? '/home/permitting' : '/rrs/home/permitting'
              }
              title='read about permitting requirements'
              content='read about permitting requirements'
              aria-label='read about permitting requirements'
            />{' '}
            or contact your{' '}
            <ExternalLink
              href='https://regulatory.ops.usace.army.mil/offices '
              title='local regulatory office'
              content='local regulatory office'
              aria-label='local regulatory office'
            />
            .
          </Alert>
          <Checkbox
            disabled={isReadOnly}
            id='check-section-404'
            label={ApplicableStatutoryAuthorities.Section404}
            labelDescription='Requires authorization from the U.S. Army Corps of Engineers, for the discharge of dredged or fill material into all waters of the United States, including wetlands. Discharges of fill material generally include, without limitation: placement of fill that is necessary for the construction of any structure, or impoundment requiring rock, sand, dirt, or other material for its construction; site-development fills for recreational, industrial, commercial, residential, and other uses; causeways or road fills; dams and dikes; artificial islands; property protection or reclamation devices such as riprap, groins, seawalls, breakwaters, and revetments; beach nourishment; levees; fill for intake and outfall pipes and subaqueous utility lines; fill associated with the creation of ponds; and any other work involving the discharge of fill or dredged material. A USACE permit is required whether the work is permanent or temporary. Examples of temporary discharges include dewatering of dredged material prior to final disposal, and temporary fills for access roadways, cofferdams, storage and work areas.'
            name='applicableStatutoryAuthority'
            onChange={handleChange}
            tile
            value={ApplicableStatutoryAuthorities.Section404}
          />
          <Checkbox
            disabled={isReadOnly}
            id='check-section-10'
            label={ApplicableStatutoryAuthorities.Section10}
            labelDescription='Requires authorization from the United States Army Corps of Engineers, for the construction of any structure in or over any navigable water of the United States. Structures or work outside the limits defined for navigable waters of the United States require a Section 10 permit if the structure or work affects the course, location, or condition of the water body. The law applies to any dredging or disposal of dredged materials, excavation, filling, re-channelization, or any other modification of a navigable water of the United States, and applies to all structures, from the smallest floating dock to the largest commercial undertaking. It further includes, without limitation, any wharf, dolphin, weir, boom breakwater, jetty, groin, bank protection (e.g. riprap, revetment, bulkhead), mooring structures such as pilings, aerial or subaqueous power transmission lines, intake or outfall pipes, permanently moored floating vessel, tunnel, artificial canal, boat ramp, aids to navigation, and any other permanent, or semi-permanent obstacle or obstruction.'
            name='applicableStatutoryAuthority'
            onChange={handleChange}
            tile
            value={ApplicableStatutoryAuthorities.Section10}
          />
          <Checkbox
            disabled={isReadOnly}
            id='check-section-103'
            label={ApplicableStatutoryAuthorities.Section103}
            labelDescription='Requires authorization from the United States Army Corps of Engineers for the disposal of dredged materials in the ocean.'
            name='applicableStatutoryAuthority'
            onChange={handleChange}
            tile
            value={ApplicableStatutoryAuthorities.Section103}
          />
        </fieldset>
        {isCoastalDistrict && (
          <>
            <div>
              <SelectInput
                className='width-50'
                fullWidth={false}
                name='isInTerrSeasOrOceanWaters'
                label='Would the project occur in the territorial seas or ocean waters?'
                required
                readOnly={isReadOnly}
                onChange={handleChange}
              >
                <option key='2' value='true'>
                  Yes
                </option>
                <option key='3' value='false'>
                  No
                </option>
              </SelectInput>
            </div>
            {isInTerrSeasOrOceanWaters === 'true' && (
              <div>
                <TextAreaInput
                  name='activityBaselineRelationship'
                  label="Describe the activity's relationship to the baseline from which the territorial sea is measured"
                  tooltip={tooltipText.activityBaselineRelationship}
                  required
                  onChange={handleChange}
                  readOnly={isReadOnly}
                />
              </div>
            )}
          </>
        )}
        {/* Discharge Section */}
        {applicableStatutoryAuthority.length > 0 &&
          applicableStatutoryAuthority?.includes(ApplicableStatutoryAuthorities.Section404) && (
            <Section label='Discharges'>
              <p className='text-bold'>Projects involving Section 404 Discharges</p>
              <PermitsDischarges handleChange={handleChange} isReadOnly={isReadOnly} />
            </Section>
          )}
        {/* Dredge Project Section */}
        {applicableStatutoryAuthority.length > 0 &&
          applicableStatutoryAuthority?.includes(ApplicableStatutoryAuthorities.Section10) && (
            <Section label='Dredging'>
              <p className='text-bold margin-bottom-1'>Projects Involving Impacts to Section 10 Navigable Waters</p>
              <div>
                <SelectInput
                  className='width-50'
                  fullWidth={false}
                  name='isDredgingNavWaters'
                  label='Does the project involve dredging in navigable waters of the United States?'
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  required
                >
                  <option key='2' value='true'>
                    Yes
                  </option>
                  <option key='3' value='false'>
                    No
                  </option>
                </SelectInput>
              </div>
              {(isDredgingNavWaters === 'true' || isDredgingNavWaters === true) && (
                <PermitsDredgeProject handleChange={handleChange} isReadOnly={isReadOnly} />
              )}
            </Section>
          )}
        {/* Dredge Transportation Section */}
        {applicableStatutoryAuthority.length > 0 &&
          applicableStatutoryAuthority?.includes(ApplicableStatutoryAuthorities.Section103) && (
            <Section label='Dredge Transportation'>
              <p className='text-bold'>
                Projects Involving the transportation of dredged material for the purpose of disposing of it in ocean
                waters
              </p>
              <Alert type='warning'>
                IMPORTANT: The physical boundaries of the disposal site must be provided in a map attached to this
                request. The specific location of the disposal of the dredged material must be itemized in the impact
                section of this request.
              </Alert>
              <PermitsDredgeTransportation handleChange={handleChange} isReadOnly={isReadOnly} />
            </Section>
          )}
      </FormProvider>
    );
  }
);

DaPermitInformation.metadata = DaPermitInformationMetadata;

export default DaPermitInformation;
